import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Add, Calendar, Teacher } from "iconsax-reactjs";
import DataTable, { Column } from "../../components/commons/DataTable";
import Button from "../../components/commons/Button";
import ChallengeChainAction from "../../components/partials/challenge/ChallengeChainAction";
import ChallengeEmptyState from "../../components/partials/challenge/ChallengeEmptyState";
import ChallengeHeaderFilter from "../../components/partials/challenge/ChallengeHeaderFilter";
import ChallengeStatusBadge from "../../components/partials/challenge/ChallengeStatusBadge";
import { getGoldenChallengeChains } from "../../services/webServices/challenge/get";
import type { GoldenChallengeChain } from "../../services/webServices/challenge/types";
import { CHALLENGE_MONTH_OPTIONS } from "../../constants/challenge";
import { getBookLabel, getGradeLabel, GRADE_OPTIONS } from "../../constants/education";
import { formatJalaliNumericDate, getJalaliMonthRange } from "../../utils/jalaliDate";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";

const PAGE_SIZE = 12;

const ALL_GRADES = [
  ...GRADE_OPTIONS.elementary,
  ...GRADE_OPTIONS.middle,
  ...GRADE_OPTIONS.high,
];

export default function Challenge() {
  const [data, setData] = useState<GoldenChallengeChain[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [gradeFilter, setGradeFilter] = useState<string>("");
  const [monthFilter, setMonthFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  const navigate = useNavigate();
  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  const fetchChains = useCallback(async () => {
    if (isFirstLoad) showLoading();
    setIsLoading(true);

    const monthRange = monthFilter
      ? getJalaliMonthRange(monthFilter)
      : { fromDate: undefined, toDate: undefined };

    const params = {
      page: currentPage,
      page_size: PAGE_SIZE,
      grade: gradeFilter ? Number(gradeFilter) : undefined,
      from_date_after: monthRange.fromDate,
      from_date_before: monthRange.toDate,
    };

    const response = await getGoldenChallengeChains({ params });

    setData(response.items);
    setTotalItems(response.count);

    setIsLoading(false);
    if (isFirstLoad) hideLoading();
  }, [currentPage, gradeFilter, isFirstLoad, monthFilter]);

  useEffect(() => {
    fetchChains();
  }, [fetchChains]);

  useEffect(() => {
    if (!isLoading && data.length === 0 && currentPage > 1) {
      setCurrentPage(1);
    }
  }, [currentPage, data.length, isLoading]);

  const handleCreate = () => navigate("/challenge/create");

  const resetPage = () => setCurrentPage(1);

  const columns: Column<GoldenChallengeChain>[] = [
    {
      key: "lesson",
      header: "درس",
      align: "right",
      width: "15%",
      accessor: (row) => getBookLabel(row.lesson),
    },
    {
      key: "grade",
      header: "پایه",
      align: "right",
      width: "15%",
      accessor: (row) =>
        row.grade?.length
          ? row.grade.map((grade) => getGradeLabel(grade)).join("، ")
          : "-",
    },
    {
      key: "from_date",
      header: "بازه اجرا",
      align: "right",
      width: "25%",
      accessor: (row) => {
        const from = formatJalaliNumericDate(row.from_date);
        const to = formatJalaliNumericDate(row.to_date);
        return from && to ? `${from} تا ${to}` : "-";
      },
    },

    {
      key: "created_at",
      header: "زمان انتشار",
      align: "right",
      width: "20%",
      accessor: (row) =>
        row.created_at ? formatJalaliNumericDate(row.created_at) : "-",
    },
  ];

  const headerActions = (
    <div className="flex items-center gap-2">
      <ChallengeHeaderFilter
        value={monthFilter}
        placeholder="ماه"
        options={CHALLENGE_MONTH_OPTIONS}
        icon={<Calendar size={18} variant="Outline" />}
        onChange={(value) => {
          setMonthFilter(value);
          resetPage();
        }}
      />
      <ChallengeHeaderFilter
        value={gradeFilter}
        placeholder="پایه"
        options={ALL_GRADES}
        icon={<Teacher size={18} variant="Outline" />}
        onChange={(value) => {
          setGradeFilter(value);
          resetPage();
        }}
      />
      <Button
        variant="primary"
        style="outline"
        size="md"
        text="زنجیره جدید"
        loading={false}
        disabled={false}
        onClick={handleCreate}
        icon={Add}
      />
    </div>
  );

  const hasActiveFilters = Boolean(gradeFilter || monthFilter);
  const isEmpty = !isLoading && data.length === 0 && !hasActiveFilters;

  if (isEmpty) {
    return <ChallengeEmptyState onCreate={handleCreate} />;
  }

  return (
    <DataTable
      title="لیست زنجیره‌های هفتگی"
      data={data}
      columns={columns}
      loading={isLoading}
      loadingText="در حال بارگذاری زنجیره‌ها..."
      emptyText="زنجیره‌ای یافت نشد"
      pagination={true}
      serverSidePagination={true}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      totalItems={totalItems}
      pageSize={PAGE_SIZE}
      headerAction={headerActions}
      renderActions={(row) => (
        <ChallengeChainAction chain={row} onActionSuccessed={fetchChains} />
      )}
    />
  );
}
